import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber, formatDate, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, RescheduleBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Search, AlertCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "@/components/DatePicker";
import { SupplierName } from "@/components/SupplierName";

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({ status: "all", supplier_id: "all", q: searchParams.get("q") || "" });
  const [open, setOpen] = useState(false);

  const loadInvoices = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.status !== "all") p.set("status", filters.status);
    if (filters.supplier_id !== "all") p.set("supplier_id", filters.supplier_id);
    if (filters.q) p.set("q", filters.q);
    api.get(`/invoices?${p.toString()}`).then((r) => setInvoices(r.data));
  }, [filters]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/fabric-types").then((r) => setFabrics(r.data));
  }, []);

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";

  return (
    <PageShell title="Faktur Pembelian">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input placeholder="Cari no. nota" className="pl-8 w-48" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="invoice-search" />
          </div>
          <Select value={filters.supplier_id} onValueChange={(v) => setFilters({ ...filters, supplier_id: v })}>
            <SelectTrigger className="w-44" data-testid="invoice-supplier-filter"><SelectValue placeholder="Supplier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Supplier</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-44" data-testid="invoice-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="UNSCHEDULED">Belum Terjadwal</SelectItem>
              <SelectItem value="SCHEDULED">Terjadwal</SelectItem>
              <SelectItem value="PAID">Lunas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <InvoiceDialog open={open} setOpen={setOpen} suppliers={suppliers} fabrics={fabrics} onSaved={loadInvoices} />
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead><TableHead>No. Nota</TableHead><TableHead>Supplier</TableHead>
                <TableHead>Tgl Nota</TableHead><TableHead className="text-right">Roll</TableHead>
                <TableHead className="text-right">Nilai Pembelian</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((i) => (
                <TableRow key={i.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/faktur/${i.id}`)} data-testid={`invoice-row-${i.code}`}>
                  <TableCell className="font-mono text-xs">{i.code}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {i.invoice_number}
                      {i.reschedule_badge && <RescheduleBadge />}
                      {i.warehouse && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[11px]" data-testid={`inv-warehouse-${i.code}`}>🏬 {i.warehouse}</Badge>}
                    </div>
                    {i.note && <div className="text-[11px] text-slate-400 mt-0.5">{i.note}</div>}
                  </TableCell>
                  <TableCell><SupplierName name={supName(i.supplier_id)} id={i.supplier_id} /></TableCell>
                  <TableCell>{formatDate(i.invoice_date)}</TableCell>
                  <TableCell className="text-right font-mono">{formatNumber(i.total_rolls)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{formatRupiah(i.total_amount)}</TableCell>
                  <TableCell><StatusBadge status={i.status} /></TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Tidak ada nota.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageShell>
  );
}

const emptyLine = () => ({ fabric_type_id: "", roll_count: "", total_length: "", unit: "yard", price_per_unit: "" });

function InvoiceDialog({ open, setOpen, suppliers, fabrics, onSaved }) {
  const [form, setForm] = useState({ supplier_id: "", invoice_number: "", invoice_date: todayWITA(), delivery_date: todayWITA(), note: "", warehouse: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [dup, setDup] = useState(null);
  const [eff, setEff] = useState(todayWITA());

  useEffect(() => {
    if (!open) return;
    api.get("/settings").then((r) => {
      const d = r.data.effective_date;
      setEff(d);
      setForm((f) => ({ ...f, invoice_date: d, delivery_date: d }));
    }).catch(() => {});
  }, [open]);

  const reset = () => {
    setForm({ supplier_id: "", invoice_number: "", invoice_date: eff, delivery_date: eff, note: "", warehouse: "" });
    setLines([emptyLine()]);
    setDup(null);
  };

  const checkDup = async () => {
    if (!form.supplier_id || !form.invoice_number) return;
    const { data } = await api.get(`/invoices/check-duplicate?supplier_id=${form.supplier_id}&invoice_number=${encodeURIComponent(form.invoice_number)}`);
    setDup(data.duplicate ? data.existing_code : null);
  };

  const lineSubtotal = (l) => (Number(l.total_length) || 0) * (Number(l.price_per_unit) || 0);
  const total = lines.reduce((a, l) => a + lineSubtotal(l), 0);
  const totalRolls = lines.reduce((a, l) => a + (Number(l.roll_count) || 0), 0);

  const submit = async () => {
    if (!form.supplier_id) return toast.error("Pilih supplier");
    if (!form.invoice_number) return toast.error("Isi nomor nota");
    const validLines = lines.filter((l) => l.fabric_type_id && Number(l.total_length) > 0);
    if (validLines.length === 0) return toast.error("Tambahkan minimal satu baris kain");
    try {
      await api.post("/invoices", {
        ...form,
        lines: validLines.map((l) => ({
          fabric_type_id: l.fabric_type_id, roll_count: Number(l.roll_count) || 0,
          total_length: Number(l.total_length), unit: l.unit, price_per_unit: Number(l.price_per_unit) || 0,
        })),
      });
      toast.success("Nota berhasil disimpan");
      setOpen(false);
      reset();
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="add-faktur-button"><Plus className="h-4 w-4 mr-1.5" /> Tambah Nota</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catat Nota Pembelian</DialogTitle><DialogDescription className="sr-only">Form pencatatan nota pembelian kain dengan beberapa baris detail</DialogDescription></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Supplier</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="faktur-supplier-select"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Nomor Nota</Label>
            <Input value={form.invoice_number} onBlur={checkDup} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="faktur-number-input" />
            {dup && <div className="text-xs text-rose-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Nomor nota sudah ada ({dup}) untuk supplier ini</div>}
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Nota</Label>
            <DatePicker value={form.invoice_date} today={eff} onChange={(v) => setForm((f) => ({ ...f, invoice_date: v }))} testid="faktur-date-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tanggal Pengiriman</Label>
            <DatePicker value={form.delivery_date} today={eff} onChange={(v) => setForm((f) => ({ ...f, delivery_date: v }))} testid="faktur-delivery-input" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Catatan Gudang (opsional)</Label>
            <Input value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} placeholder="mis. Gudang Timur / Langsung ke customer (tidak masuk gudang)" data-testid="faktur-warehouse-input" />
          </div>
        </div>

        <div className="space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <Label>Detail Kain</Label>
            <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, emptyLine()])} data-testid="add-line-btn"><Plus className="h-3.5 w-3.5 mr-1" />Baris</Button>
          </div>
          <div className="space-y-2">
            {lines.map((l, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-2xl" data-testid={`fabric-item-row-${idx}`}>
                <div className="col-span-12 sm:col-span-3">
                  <Label className="text-[11px]">Jenis Kain</Label>
                  <Select value={l.fabric_type_id} onValueChange={(v) => setLines(lines.map((x, i) => i === idx ? { ...x, fabric_type_id: v } : x))}>
                    <SelectTrigger data-testid={`line-fabric-${idx}`}><SelectValue placeholder="Kain" /></SelectTrigger>
                    <SelectContent>{fabrics.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 sm:col-span-2"><Label className="text-[11px]">Roll</Label><Input type="number" value={l.roll_count} onChange={(e) => setLines(lines.map((x, i) => i === idx ? { ...x, roll_count: e.target.value } : x))} data-testid={`line-roll-${idx}`} /></div>
                <div className="col-span-4 sm:col-span-2"><Label className="text-[11px]">Panjang</Label><Input type="number" value={l.total_length} onChange={(e) => setLines(lines.map((x, i) => i === idx ? { ...x, total_length: e.target.value } : x))} data-testid={`line-length-${idx}`} /></div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[11px]">Satuan</Label>
                  <Select value={l.unit} onValueChange={(v) => setLines(lines.map((x, i) => i === idx ? { ...x, unit: v } : x))}>
                    <SelectTrigger data-testid={`line-unit-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="yard">Yard</SelectItem><SelectItem value="meter">Meter</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 sm:col-span-3"><Label className="text-[11px]">Harga/Satuan</Label><Input type="number" value={l.price_per_unit} onChange={(e) => setLines(lines.map((x, i) => i === idx ? { ...x, price_per_unit: e.target.value } : x))} data-testid={`line-price-${idx}`} /></div>
                <div className="col-span-12 flex items-center justify-between gap-2 border-t border-slate-200/70 pt-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-slate-500">Subtotal</span>
                    <span className="font-mono text-sm font-bold text-slate-900" data-testid={`line-subtotal-${idx}`}>{formatRupiah(lineSubtotal(l))}</span>
                  </div>
                  {lines.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setLines(lines.filter((_, i) => i !== idx))} data-testid={`remove-line-${idx}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Catatan (opsional)</Label>
          <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="faktur-note-input" />
        </div>

        <div className="flex items-center justify-between total-box rounded-2xl p-4">
          <div><div className="text-xs text-slate-300">Total Roll</div><div className="font-mono font-bold text-lg">{formatNumber(totalRolls)}</div></div>
          <div className="text-right"><div className="text-xs text-slate-300">Total Nota</div><div className="font-mono font-bold text-lg" data-testid="faktur-total-amount">{formatRupiah(total)}</div></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="faktur-cancel-button">Batal</Button>
          <Button onClick={submit} data-testid="faktur-submit-button">Simpan Nota</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
